import debounce from 'lodash/debounce';
import { Controller } from 'stimulus';

const DEBOUNCE_TIME_MS = 50;
const CLASS_OVERFLOWS = 'amba-overflows';

/**
 * Detects when total child content is wider than parent's available width,
 * add adds an overflow class.
 *
 * The children must be first ancestors of `inlineContainerTarget`.
 * Their total width is calculated on page load instead of using the parent's `scrollWidth`,
 * because the children can wrap causing this value to change.
 *
 * Child widths are recalculated on mutation change to account for content changes
 * causing widths to change.
 *
 * A threshold value can be set to change how soon the elements are deemed overflowing.
 */
export default class extends Controller {
  static targets = ['inlineContainer'];
  static values = {
    threshold: { type: Number, default: 24 },
  };

  connect() {
    // Compute total width once
    this.inlineActionsSize = this.#computeInlineActionsSize();

    // Check for overflow on load after next frame to let Stimulus finish connecting
    // all its controllers
    requestAnimationFrame(() => {
      this.#updateOverflowState();

      // Recalculate width on content change
      this.#startInlineObserver();
    });

    // Recalculate width on resize
    this.handleResize = debounce(
      this.#updateOverflowState.bind(this),
      DEBOUNCE_TIME_MS
    );
    window.addEventListener('resize', this.handleResize);
  }

  disconnect() {
    window.removeEventListener('resize', this.handleResize);
    this.#stopInlineObserver();
  }

  #startInlineObserver() {
    // Recompute widths when inline content changes (e.g., button text updates)
    this.handleInlineContentChange = debounce(() => {
      this.inlineActionsSize = this.#computeInlineActionsSize();
      this.#updateOverflowState();
    }, DEBOUNCE_TIME_MS);

    this.inlineObserver = new MutationObserver(this.handleInlineContentChange);
    this.inlineObserver.observe(this.inlineContainerTarget, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }

  #stopInlineObserver() {
    if (!this.inlineObserver) return;
    this.inlineObserver.disconnect();
    this.inlineObserver = null;
  }

  #updateOverflowState() {
    const isOverflowing =
      this.inlineActionsSize + this.thresholdValue > this.element.scrollWidth;
    this.element.classList.toggle(CLASS_OVERFLOWS, isOverflowing);
  }

  #computeInlineActionsSize() {
    // Total the widths of all children, including all box model properties
    const childrenWidths = Array.from(this.inlineContainerTarget.children).map(
      (el) => {
        const width = el.getBoundingClientRect().width;

        // JS box model API methods don't include margins, so find those manually
        const styles = getComputedStyle(el);
        const margins =
          parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);

        return Math.ceil(width + margins);
      }
    );

    return childrenWidths.reduce((sum, n) => sum + n, 0);
  }
}
